import classNames from 'classnames'
import React, { ReactNode } from 'react'

import { Checkbox, CheckboxProps } from '../checkbox/checkbox.js'
import { Radio, RadioProps } from '../radio/radio.js'

export type ChoiceGroupValue = string | string[] | null

export interface ChoiceGroupProps {
    id: string
    type: 'radio' | 'checkbox'
    label: ReactNode
    name: string
    options: CheckboxProps[] | RadioProps[]
    className?: string
    value?: ChoiceGroupValue
    onChange?: (value: ChoiceGroupValue) => void
}

export const ChoiceGroup = (props: ChoiceGroupProps): JSX.Element => {
    const { label, id, options, type, value } = props

    const className: string = classNames('choice-group', props.className)

    const handleChange = (val: string, checked: boolean, onChange?: (checked: boolean) => void): void => {
        let newValue = value

        if (type === 'checkbox') {
            newValue = newValue || []

            if (Array.isArray(newValue)) {
                if (checked && !newValue.includes(val)) {
                    newValue.push(val)
                } else {
                    newValue = newValue.filter((item) => item !== val)
                }
            }
        } else {
            newValue = val
        }

        if (props.onChange) {
            props.onChange(newValue)
        }

        if (onChange) {
            onChange(checked)
        }
    }

    const isChecked = (val: string): boolean => {
        const currentValue = value

        if (type === 'checkbox' && currentValue) {
            return val === currentValue || currentValue.indexOf(val) !== -1
        } else {
            return val === currentValue
        }
    }

    const getOptionInput = (option: CheckboxProps | RadioProps): React.InputHTMLAttributes<HTMLInputElement> => {
        return {
            ...option.input,
            name: props.name,
        }
    }

    const renderChildren = () => {
        return options.map((item: CheckboxProps | RadioProps) => {
            const val = item.value || 'on'
            const onChange = (checked: boolean) => handleChange(val, checked, item.onChange)

            if (type === 'checkbox') {
                return (
                    <Checkbox
                        {...(item as CheckboxProps)}
                        checked={isChecked(item.value)}
                        className="choice-group__item"
                        input={getOptionInput(item)}
                        key={item.id}
                        onChange={onChange}
                    />
                )
            } else {
                return (
                    <Radio
                        {...(item as RadioProps)}
                        checked={isChecked(item.value)}
                        className="choice-group__item"
                        input={getOptionInput(item)}
                        key={item.id}
                        onChange={onChange}
                    />
                )
            }
        })
    }

    return (
        <fieldset className={className} id={id}>
            <legend className="choice-group__label bold">{label}</legend>
            <div className="choice-group__inner">{renderChildren()}</div>
        </fieldset>
    )
}
